/**
 * Utility functions for sending SMS messages
 */
import { genericPhpApi } from "@/integrations/php/client";

const getAppBaseUrl = (): string => window.location.origin;

/**
 * Sends an SMS notification using the PHP API
 * @param phone The recipient's phone number
 * @param message The message content
 * @param leadId Optional lead ID to associate with the message
 * @param partnerId Optional partner ID to associate with the message
 * @returns Promise with the API response
 */
export const sendSMS = async (
  phone: string,
  message: string,
  leadId?: string,
  partnerId?: string
): Promise<{ status: string; messageId?: string; error?: string }> => {
  try {
    // Generate a tracking ID for this SMS
    const trackingId = `sms-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    // Call the PHP API to send the SMS (session + CSRF auth)
    const csrfToken = sessionStorage.getItem('csrf_token') || '';
    const response = await fetch('/api/send-sms.php', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
      },
      body: JSON.stringify({
        phone,
        message,
        leadId,
        partnerId,
        trackingId
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    
    // Log SMS event if we have both lead and partner IDs
    if (leadId && partnerId) {
      try {
        await genericPhpApi
          .from('sms_logs')
          .insert([{
            lead_id: leadId,
            partner_id: partnerId,
            status: result.status === 'sent' ? 'sent' : 'failed',
            message_id: result.messageId || null,
            timestamp: new Date().toISOString()
          }]);
      } catch (error) {
        console.error('Error logging SMS:', error);
      }
    }
    
    return result;
  } catch (error) {
    console.error('Error sending SMS:', error);
    return {
      status: 'failed',
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

/**
 * Creates a partner notification message for new leads
 * @param leadName The lead's name
 * @param service The requested service
 * @param leadId The lead ID for accessing details
 * @param address Optional lead address
 * @param timeframe Optional timeframe information
 * @returns Formatted partner notification message
 */
export const createPartnerNotificationMessage = (
  leadName: string, 
  service: string, 
  leadId: string,
  address?: string,
  timeframe?: string
): string => {
  // Format the service name for display with additional emoji based on service type
  let serviceEmoji = "🏠";
  const formattedService = service
    .replace(/([A-Z])/g, ' $1') // Add space before capital letters
    .replace(/^./, (str) => str.toUpperCase()); // Capitalize first letter
  
  // Add emoji based on service
  switch (service.toLowerCase()) {
    case "εξοικονομώ":
      serviceEmoji = "🏠";
      break;
    case "αντλίεςθερμότητας":
      serviceEmoji = "♨️";
      break;
    case "φωτοβολταϊκά":
      serviceEmoji = "☀️";
      break;
    case "κλιματιστικά":
      serviceEmoji = "❄️";
      break;
    case "φυσικόαέριο":
      serviceEmoji = "🔥";
      break;
    case "ηλιακά":
      serviceEmoji = "🌞";
      break;
    case "ανακαινίζωενεργειακά":
      serviceEmoji = "⚡";
      break;
    case "ανακαινίζωπλήρηςανακαίνιση":
      serviceEmoji = "🛠️";
      break;
    default:
      serviceEmoji = "🏠";
  }

  return `Έχετε νέο ενδιαφερόμενο! Δείτε: ${getAppBaseUrl()}/lead/${leadId}`;
};

/**
 * Track SMS click events
 * @param trackingId The SMS tracking ID
 * @param leadId The lead ID
 * @param event The event type
 * @param partnerId Optional partner ID
 */
export const trackSMSClick = async (
  trackingId: string,
  leadId: string,
  event: string = 'click',
  partnerId?: string
): Promise<{ status: string; message?: string; error?: string }> => {
  try {
    const response = await fetch('/api/track-sms.php', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': sessionStorage.getItem('csrf_token') || '',
      },
      body: JSON.stringify({
        trackingId,
        leadId,
        partnerId,
        event,
        timestamp: new Date().toISOString()
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error tracking SMS click:', error);
    return {
      status: 'failed',
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

/**
 * Get SMS logs from the backend API
 * @param partnerId Optional partner ID filter
 * @param leadId Optional lead ID filter
 * @returns Promise with SMS logs
 */
export const getSMSLogs = async (partnerId?: string, leadId?: string): Promise<any[]> => {
  try {
    let url = '/api/get-sms-logs.php';
    const params = new URLSearchParams();
    
    if (partnerId) params.append('partnerId', partnerId);
    if (leadId) params.append('leadId', leadId);
    
    if (params.toString()) {
      url = `${url}?${params.toString()}`;
    }
    
    const csrfToken = sessionStorage.getItem('csrf_token') || '';
    const response = await fetch(url, {
      credentials: 'include',
      headers: { 'X-CSRF-Token': csrfToken },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching SMS logs:', error);
    return [];
  }
};

/**
 * Checks if the current time is within business hours (8AM-8PM Greek time)
 * @returns Boolean indicating if it's currently business hours
 */
export const isBusinessHours = (): boolean => {
  // Create a date object with Greece timezone (Europe/Athens)
  const options = { timeZone: 'Europe/Athens' };
  const now = new Date().toLocaleString('en-US', options);
  const greekTime = new Date(now);

  const hour = greekTime.getHours();
  return hour >= 8 && hour < 20;
};

/**
 * Get formatted greek time for display
 * @returns Current time in Greece formatted as HH:MM
 */
export const getCurrentGreekTime = (): string => {
  const options = { 
    timeZone: 'Europe/Athens',
    hour: '2-digit' as '2-digit',
    minute: '2-digit' as '2-digit'
  };
  
  return new Date().toLocaleString('el-GR', options);
};
